import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";
import { SERVICES } from "../lib/site";

const navLink = ({ isActive }) =>
  `link-underline text-[12px] font-medium uppercase tracking-[0.22em] transition-colors duration-300 ${
    isActive ? "text-gold" : "text-white/80 hover:text-white"
  }`;

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className="absolute inset-x-0 top-0 z-50 bg-gradient-to-b from-navy/80 to-transparent"
      >
        <div className="container-ek flex h-20 items-center justify-between">
          <Logo />

          <nav className="hidden items-center gap-9 lg:flex" data-testid="desktop-nav">
            <div className="group relative">
              <button
                data-testid="nav-services-trigger"
                className="flex items-center gap-1.5 text-[12px] font-medium uppercase tracking-[0.22em] text-white/80 transition-colors duration-300 hover:text-white"
              >
                Services
                <ChevronDown className="h-3.5 w-3.5 text-gold transition-transform duration-300 group-hover:rotate-180" />
              </button>
              <div className="invisible absolute left-1/2 top-full -translate-x-1/2 translate-y-2 pt-5 opacity-0 transition-all duration-300 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                <div className="w-80 border border-white/10 bg-navy2/95 p-2 shadow-2xl backdrop-blur-xl">
                  {SERVICES.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      data-testid={`nav-service-${s.slug}`}
                      className="group/item flex items-center gap-4 px-4 py-3 transition-colors duration-300 hover:bg-white/5"
                    >
                      <s.icon className="h-4 w-4 shrink-0 text-gold" />
                      <span className="text-sm text-white/85 group-hover/item:text-white">
                        {s.name}
                      </span>
                      <ArrowUpRight className="ml-auto h-3.5 w-3.5 text-gold/0 transition-all duration-300 group-hover/item:text-gold" />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            <NavLink to="/about" data-testid="nav-about" className={navLink}>
              About
            </NavLink>
            <NavLink to="/gallery" data-testid="nav-gallery" className={navLink}>
              Gallery
            </NavLink>
            <NavLink to="/contact" data-testid="nav-contact" className={navLink}>
              Contact
            </NavLink>
          </nav>

          <div className="flex items-center gap-4">
            <Link
              to="/contact"
              data-testid="nav-quote-cta"
              className="hidden bg-gold px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-navy transition-all duration-300 hover:bg-gold2 lg:inline-flex"
            >
              Get a Free Quote
            </Link>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen(!open)}
              className="flex h-11 w-11 items-center justify-center border border-white/15 text-white transition-colors hover:border-gold hover:text-gold lg:hidden"
              aria-label={open ? "Close menu" : "Open menu"}
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col overflow-y-auto bg-navy/95 pt-28 backdrop-blur-xl lg:hidden"
          >
            <div className="container-ek flex flex-col gap-1 pb-16">
              <p className="eyebrow mb-3">Services</p>
              {SERVICES.map((s, i) => (
                <motion.div
                  key={s.slug}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.05, duration: 0.5 }}
                >
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`mobile-nav-service-${s.slug}`}
                    className="flex items-center justify-between border-b border-white/10 py-4"
                  >
                    <span className="font-serif text-2xl text-white">{s.name}</span>
                    <ArrowUpRight className="h-4 w-4 text-gold" />
                  </Link>
                </motion.div>
              ))}
              <div className="mt-8 flex flex-col gap-4">
                {[
                  { to: "/about", label: "About Us", id: "about" },
                  { to: "/gallery", label: "Gallery", id: "gallery" },
                  { to: "/contact", label: "Contact", id: "contact" },
                ].map((l, i) => (
                  <motion.div
                    key={l.id}
                    initial={{ opacity: 0, x: -24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 + i * 0.06, duration: 0.5 }}
                  >
                    <Link
                      to={l.to}
                      data-testid={`mobile-nav-${l.id}`}
                      className="text-sm font-medium uppercase tracking-[0.22em] text-white/80"
                    >
                      {l.label}
                    </Link>
                  </motion.div>
                ))}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                  className="mt-4"
                >
                  <Link
                    to="/contact"
                    data-testid="mobile-nav-quote-cta"
                    className="btn-gold w-full"
                  >
                    Get a Free Quote
                  </Link>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
